import { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import ErrorMessage from "../../moleculas/ErrorMessage";
import Loader from "../../moleculas/Loader";
import Paginator from "../../moleculas/Paginator";
import StatisticsCategories from "../../organims/Statistics/StatisticsCategory";
import StatisticsDescription from "../../organims/Statistics/StatisticsDescription";
import StatisticsSAHM from "../../organims/Statistics/StatisticsSAHM";
import CmsPublicApiClient, {
  CmsApiError,
} from "../../../services/api/cmsPublicApiClient";
import { mapOrganizationToVM } from "../../../services/mappers/cmsPublicMappers";
import { STATISTICS_DETAIL } from "../../../constants/routes";
import { OrganizationNodesVM } from "../../../types/viewModels/cmsPublic";

const service = new CmsPublicApiClient();
const PAGE_SIZE = 9;

const buildErrorMessage = (error: unknown) => {
  if (error instanceof CmsApiError) {
    if (error.status === 404) {
      return "No se encontraron organizaciones publicadas.";
    }

    if (error.status === 400 || error.status === 422) {
      return "La consulta de organizaciones no es válida.";
    }

    return "No fue posible cargar las organizaciones desde CMS.";
  }

  return "Ocurrió un error inesperado al cargar organizaciones.";
};

const Statistics = () => {
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<OrganizationNodesVM[]>([]);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchOrganizations = useCallback(async (nextPage = 1) => {
    setLoading(true);
    setError("");

    try {
      const response = await service.getOrganizations(nextPage, PAGE_SIZE);
      setOrganizations(response.data.map(mapOrganizationToVM));
      setPage(response.meta.pagination.page);
      setTotalPages(Math.max(response.meta.pagination.totalPages, 1));
    } catch (requestError) {
      setOrganizations([]);
      setError(buildErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations(1);
  }, [fetchOrganizations]);

  return (
    <div className="w-100 d-flex justify-content-md-start justify-content-lg-start flex-column">
      <div className="w-100 d-flex flex-column align-items-center">
        <StatisticsDescription />
      </div>
      <div className="w-100 my-5 d-flex flex-column align-items-center">
        <div className="w-75 d-flex flex-column align-items-center gap-4">
          {loading ? <Loader /> : null}

          {!loading && error ? (
            <div className="d-flex flex-column align-items-center gap-3">
              <ErrorMessage message={error} />
              <Button
                onClick={() => fetchOrganizations(page)}
                className="custom-primary-button"
              >
                Reintentar
              </Button>
            </div>
          ) : null}

          {!loading && !error && organizations.length === 0 ? (
            <ErrorMessage message="No hay organizaciones publicadas para mostrar." />
          ) : null}

          {!loading && !error && organizations.length > 0 ? (
            <>
              <StatisticsCategories
                categories={organizations.map((organization) => ({
                  id: organization.id,
                  name: organization.name,
                  icon_url: organization.iconSrc,
                  open_data_link: STATISTICS_DETAIL.replace(
                    ":id",
                    encodeURIComponent(organization.slug)
                  ),
                  metadata: {
                    description: organization.subtitle || organization.description,
                  },
                }))}
              />
              <Paginator
                currentPage={page}
                onPageChange={(nextPage: number) => fetchOrganizations(nextPage)}
                totalPages={totalPages}
              />
            </>
          ) : null}
        </div>
      </div>
      <div className="w-100 d-flex flex-column align-items-center">
        <StatisticsSAHM />
      </div>
    </div>
  );
};

export default Statistics;
